import { useEffect, useMemo, useState } from 'react'

import { Button, Modal, Typography } from '@it-incubator/ui-kit'
import { PublicVisibilitySelector } from 'common/components/PublicVisibilitySelector'

import s from './EditMDXModal.module.scss'

import {
  NoteType,
  PublicVisibility,
  useAddMDXNoteMutation,
  useDeleteMDXNoteMutation,
  useGetVideoByIdQuery,
  useUpdateMainMDXNoteMutation,
  useUpdatePreviewMDXNoteMutation,
} from '../../service'
import { MdxPreviewAndFileSelector } from './MdxPreviewAndFileSelector/MdxPreviewAndFileSelector'

type Props = {
  onClose: () => void
  videoId: string
}

export const EditMDXModal = ({ onClose, videoId }: Props) => {
  const [selectedMainMdxFilePath, setSelectedMainMdxFilePath] = useState('')
  const [selectedPreviewMdxFilePath, setSelectedPreviewMdxFilePath] = useState('')
  const [publicVisibility, setPublicVisibility] = useState<PublicVisibility>(PublicVisibility.None)
  const [addMDXNote] = useAddMDXNoteMutation()
  const [updateMainMDXNote] = useUpdateMainMDXNoteMutation()
  const [updatePreviewMDXNote] = useUpdatePreviewMDXNoteMutation()
  const [deleteMDXNote] = useDeleteMDXNoteMutation()
  const { data: video } = useGetVideoByIdQuery({ id: videoId })

  const mainMdxNote = useMemo(() => {
    return video?.mdxNotes.find(note => note.type === NoteType.Main)
  }, [video])

  const previewMdxNote = useMemo(() => {
    return video?.mdxNotes.find(note => note.type === NoteType.Preview)
  }, [video])

  // Устанавливаем начальные значения
  useEffect(() => {
    if (mainMdxNote) {
      setSelectedMainMdxFilePath(mainMdxNote.mdxFilePath)
      setPublicVisibility(mainMdxNote.publicVisibility)
    }

    if (previewMdxNote) {
      setSelectedPreviewMdxFilePath(previewMdxNote.mdxFilePath)
    }
  }, [mainMdxNote, previewMdxNote])

  // MdxFilePath или publicVisibility изменились
  const isMainMdxNoteChanged =
    (mainMdxNote && selectedMainMdxFilePath !== mainMdxNote.mdxFilePath) ||
    (mainMdxNote && mainMdxNote.publicVisibility !== publicVisibility)

  const isPreviewMdxNoteChanged =
    previewMdxNote && selectedPreviewMdxFilePath !== previewMdxNote.mdxFilePath

  // Выбран MdxFilePath, но изначально не было конспекта
  const isMainMdxNoteAdded = selectedMainMdxFilePath && !mainMdxNote
  const isPreviewMdxNoteAdded = selectedPreviewMdxFilePath && !previewMdxNote

  const isSaveButtonDisabled =
    !isMainMdxNoteChanged &&
    !isPreviewMdxNoteChanged &&
    !isMainMdxNoteAdded &&
    !isPreviewMdxNoteAdded

  const handleSave = async () => {
    const tasks: Promise<void>[] = []

    if (isMainMdxNoteChanged) {
      if (selectedMainMdxFilePath === '') {
        tasks.push(deleteMDXNote({ videoId, noteType: NoteType.Main }).unwrap())
      } else {
        tasks.push(
          updateMainMDXNote({
            videoId,
            body: { mdxFilePath: selectedMainMdxFilePath, publicVisibility },
          }).unwrap()
        )
      }
    }

    if (isMainMdxNoteAdded) {
      tasks.push(
        addMDXNote({
          videoId,
          body: { mdxFilePath: selectedMainMdxFilePath, type: NoteType.Main, publicVisibility },
        }).unwrap()
      )
    }

    if (isPreviewMdxNoteChanged) {
      if (selectedPreviewMdxFilePath === '') {
        tasks.push(deleteMDXNote({ videoId, noteType: NoteType.Preview }).unwrap())
      } else {
        tasks.push(
          updatePreviewMDXNote({
            videoId,
            body: { mdxFilePath: selectedPreviewMdxFilePath },
          }).unwrap()
        )
      }
    }

    if (isPreviewMdxNoteAdded) {
      tasks.push(
        addMDXNote({
          videoId,
          body: { mdxFilePath: selectedPreviewMdxFilePath, type: NoteType.Preview },
        }).unwrap()
      )
    }

    await Promise.all(tasks)

    onClose()
  }

  return (
    <Modal open onClose={onClose} title={'Прикрепить конспект'} className={s.modal}>
      <Typography.H2 mb={24}>Основной конспект</Typography.H2>

      <PublicVisibilitySelector value={publicVisibility} onChange={setPublicVisibility} />

      <MdxPreviewAndFileSelector
        isEditing
        selectedMdx={selectedMainMdxFilePath}
        setSelectedMdx={setSelectedMainMdxFilePath}
      />

      <Typography.H2 mt={24}>Превью конспекта</Typography.H2>

      <MdxPreviewAndFileSelector
        isEditing
        selectedMdx={selectedPreviewMdxFilePath}
        setSelectedMdx={setSelectedPreviewMdxFilePath}
      />

      <div className={s.buttons}>
        <Button variant={'secondary'} color={'primary'} onClick={onClose}>
          Отменить
        </Button>
        <Button color={'primary'} onClick={handleSave} disabled={isSaveButtonDisabled}>
          Сохранить
        </Button>
      </div>
    </Modal>
  )
}
